import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError } from "@/api/client";
import { commandesService } from "@/api/services";
import { DataTable, type Column } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { ErrorState } from "@/components/common/ErrorState";
import { LoadingState } from "@/components/common/LoadingState";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import {
  commandePeutEtreReservee,
  creerTentativeReservation,
  messageErreurReservation,
} from "@/features/commandes/reservation";
import { useI18n } from "@/i18n/I18nProvider";
import type {
  CommandeDetail,
  CommandeReservationPayload,
  CommandeReservationResponse,
  LigneCommande,
} from "@/types/domain";

const EMPTY_VALUE = "—";

export default function CommandeDetailPage() {
  const { commandeId } = useParams<{ commandeId: string }>();
  const { t, lang } = useI18n();
  const [commande, setCommande] = useState<CommandeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tentativeReservation, setTentativeReservation] =
    useState<CommandeReservationPayload | null>(null);
  const [reservationEnCours, setReservationEnCours] = useState(false);
  const [reservationError, setReservationError] = useState<string | null>(null);
  const [reservationResult, setReservationResult] =
    useState<CommandeReservationResponse | null>(null);

  const locale = lang === "fr" ? "fr-CA" : "en-CA";

  const formatCurrency = (value: number | null | undefined) =>
    value == null || !Number.isFinite(value)
      ? EMPTY_VALUE
      : new Intl.NumberFormat(locale, {
          style: "currency",
          currency: "CAD",
          maximumFractionDigits: 2,
        }).format(value);

  const formatDate = (value: string | null | undefined, withTime = false) => {
    if (!value) return EMPTY_VALUE;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return EMPTY_VALUE;
    return withTime ? date.toLocaleString(locale) : date.toLocaleDateString(locale);
  };

  const displayValue = (value: string | number | null | undefined) =>
    value === null || value === undefined || value === "" ? EMPTY_VALUE : value;

  const displayBoolean = (value: boolean | null | undefined) =>
    value == null ? (
      EMPTY_VALUE
    ) : (
      <StatusBadge
        status={value ? "Oui" : "Non"}
        tone={value ? "success" : "neutral"}
      />
    );

  const loadCommande = useCallback(async () => {
    if (!commandeId) {
      setError("Identifiant commande manquant.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await commandesService.detail(commandeId);
      setCommande(data);
    } catch (err) {
      console.error("Erreur chargement fiche commande :", err);
      setCommande(null);
      setError(
        err instanceof ApiError && err.status === 404
          ? "Commande introuvable."
          : "Impossible de charger la fiche commande.",
      );
    } finally {
      setLoading(false);
    }
  }, [commandeId]);

  useEffect(() => {
    loadCommande();
  }, [loadCommande]);

  const preparerReservation = () => {
    if (!commandeId) return;
    try {
      setTentativeReservation(creerTentativeReservation(commandeId));
      setReservationError(null);
      setReservationResult(null);
    } catch (err) {
      setReservationError(messageErreurReservation(err));
    }
  };

  const annulerReservation = () => {
    if (reservationEnCours) return;
    setTentativeReservation(null);
    setReservationError(null);
  };

  const confirmerReservation = async () => {
    if (!commandeId || !tentativeReservation || reservationEnCours) return;

    try {
      setReservationEnCours(true);
      setReservationError(null);
      const resultat = await commandesService.reserver(
        commandeId,
        tentativeReservation,
      );
      setReservationResult(resultat);
      setTentativeReservation(null);
      await loadCommande();
    } catch (err) {
      console.error("Erreur réservation commande :", err);
      setReservationError(messageErreurReservation(err));
    } finally {
      setReservationEnCours(false);
    }
  };

  const lineColumns: Column<LigneCommande>[] = [
    {
      key: "sku",
      header: "SKU",
      mono: true,
      render: (ligne) => displayValue(ligne.sku),
    },
    {
      key: "description",
      header: "Description",
      render: (ligne) => displayValue(ligne.description),
    },
    {
      key: "quantite_demandee",
      header: "Demandée",
      align: "right",
      mono: true,
      render: (ligne) => displayValue(ligne.quantite_demandee),
    },
    {
      key: "prix_unitaire_po",
      header: "Prix PO",
      align: "right",
      mono: true,
      render: (ligne) => formatCurrency(ligne.prix_unitaire_po),
    },
    {
      key: "prix_unitaire_officiel",
      header: "Prix officiel",
      align: "right",
      mono: true,
      render: (ligne) => formatCurrency(ligne.prix_unitaire_officiel),
    },
    {
      key: "quantite_disponible",
      header: "Disponible",
      align: "right",
      mono: true,
      render: (ligne) => displayValue(ligne.quantite_disponible),
    },
    {
      key: "quantite_reservee",
      header: "Réservée",
      align: "right",
      mono: true,
      render: (ligne) => displayValue(ligne.quantite_reservee),
    },
    {
      key: "quantite_a_acheter",
      header: "À acheter",
      align: "right",
      mono: true,
      render: (ligne) => displayValue(ligne.quantite_a_acheter),
    },
    {
      key: "besoin_achat",
      header: "Besoin d’achat",
      render: (ligne) =>
        ligne.besoin_achat ? (
          <div className="space-y-1">
            <StatusBadge status={ligne.besoin_achat.statut} />
            <p className="font-mono text-[11px] text-muted-foreground">
              {ligne.besoin_achat.quantite_a_acheter} unité(s)
            </p>
          </div>
        ) : (
          EMPTY_VALUE
        ),
    },
    {
      key: "quantite_expediee",
      header: "Expédiée",
      align: "right",
      mono: true,
      render: (ligne) => displayValue(ligne.quantite_expediee),
    },
    {
      key: "statut",
      header: "Statut",
      render: (ligne) =>
        ligne.statut ? <StatusBadge status={ligne.statut} /> : EMPTY_VALUE,
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6" data-testid="commande-detail-page">
        <PageHeader
          title="Détail de la commande"
          subtitle="Chargement des informations de la commande."
          breadcrumb={[t.appSuffix, t.modules.commandes.title, "Détail"]}
        />
        <section className="ops-panel">
          <LoadingState label="Chargement de la fiche commande..." rows={7} />
        </section>
      </div>
    );
  }

  if (error || !commande) {
    return (
      <div className="space-y-6" data-testid="commande-detail-page">
        <PageHeader
          title="Détail de la commande"
          breadcrumb={[t.appSuffix, t.modules.commandes.title, "Détail"]}
        />
        <ErrorState
          title={error === "Commande introuvable." ? "Commande introuvable" : "Chargement impossible"}
          description={error ?? "Commande introuvable."}
          retryLabel="Réessayer"
          onRetry={commandeId ? loadCommande : undefined}
        />
        <Link
          to="/commandes"
          className="ops-focus inline-flex rounded-sm text-sm font-medium text-muted-foreground hover:text-foreground hover:underline"
        >
          ← Retour aux commandes
        </Link>
      </div>
    );
  }

  const lignes = Array.isArray(commande.lignes) ? commande.lignes : [];
  const generalFields = [
    ["N° commande", displayValue(commande.numero_commande), true],
    ["Client", displayValue(commande.client?.nom_entreprise)],
    ["N° PO client", displayValue(commande.numero_po_client), true],
    ["Courriel acheteur", displayValue(commande.courriel_acheteur)],
    ["Date de réception", formatDate(commande.date_reception)],
    ["Valeur de la commande", formatCurrency(commande.valeur_commande), true],
    ["Approbateur", displayValue(commande.approbateur)],
    ["Commande Sage", displayValue(commande.numero_commande_sage), true],
    ["Facture Sage", displayValue(commande.numero_facture_sage), true],
    ["Date d’expédition", formatDate(commande.date_expedition)],
    ["Preuve d’expédition", displayValue(commande.preuve_expedition)],
    ["Date d’envoi facture", formatDate(commande.date_envoi_facture)],
    ["Créée le", formatDate(commande.cree_le, true)],
    ["Modifiée le", formatDate(commande.modifie_le, true)],
  ] as const;

  return (
    <div className="space-y-6" data-testid="commande-detail-page">
      <Link
        to="/commandes"
        className="ops-focus inline-flex rounded-sm text-sm font-medium text-muted-foreground hover:text-foreground hover:underline"
      >
        ← Retour aux commandes
      </Link>

      <PageHeader
        title={displayValue(commande.numero_commande).toString()}
        subtitle={displayValue(commande.client?.nom_entreprise).toString()}
        breadcrumb={[
          t.appSuffix,
          t.modules.commandes.title,
          displayValue(commande.numero_commande).toString(),
        ]}
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <StatusBadge status={commande.statut || "—"} />
            {commandePeutEtreReservee(commande.statut) &&
              !tentativeReservation && (
                <button
                  type="button"
                  className="ops-focus rounded-sm bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                  onClick={preparerReservation}
                  data-testid="prepare-reservation-button"
                >
                  Réserver le stock disponible
                </button>
              )}
          </div>
        }
      />

      {(tentativeReservation || reservationError || reservationResult) && (
        <section
          className="ops-panel border-l-4 border-l-primary p-5"
          data-testid="reservation-panel"
        >
          <h2 className="text-base font-semibold">Réservation transactionnelle</h2>

          {tentativeReservation && (
            <>
              <p className="mt-2 text-sm text-muted-foreground">
                Confirmez la réservation du stock actuellement disponible pour
                toutes les lignes. Tout manque créera automatiquement un besoin
                d’achat traçable, sans choisir de fournisseur.
              </p>
              <p className="mt-2 font-mono text-[11px] text-muted-foreground">
                Clé de tentative : {tentativeReservation.idempotency_key}
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  className="ops-focus rounded-sm bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={confirmerReservation}
                  disabled={reservationEnCours}
                  data-testid="confirm-reservation-button"
                >
                  {reservationEnCours
                    ? "Réservation en cours..."
                    : reservationError
                      ? "Réessayer avec la même clé"
                      : "Confirmer la réservation"}
                </button>
                <button
                  type="button"
                  className="ops-focus rounded-sm border border-border bg-card px-3 py-2 text-sm font-semibold hover:bg-[hsl(var(--surface-raised))] disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={annulerReservation}
                  disabled={reservationEnCours}
                >
                  Annuler
                </button>
              </div>
            </>
          )}

          {reservationError && (
            <p className="mt-3 text-sm text-destructive" role="alert">
              {reservationError}
            </p>
          )}

          {reservationResult && !tentativeReservation && (
            <p className="mt-2 text-sm" role="status">
              Réservation confirmée : {reservationResult.quantite_reservee} unité(s)
              réservée(s), {reservationResult.quantite_a_acheter} unité(s) à acheter,
              {" "}{reservationResult.besoins_achat.length} besoin(s) actif(s).
            </p>
          )}
        </section>
      )}

      <section className="ops-panel p-5" data-testid="commande-general-information">
        <div className="mb-5">
          <h2 className="text-lg font-semibold">Informations générales</h2>
          <p className="text-sm text-muted-foreground">
            Données fournies par la fiche commande du backend.
          </p>
        </div>

        <dl className="grid gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <div>
            <dt className="ops-label">Statut</dt>
            <dd className="mt-1.5"><StatusBadge status={commande.statut || "—"} /></dd>
          </div>
          {generalFields.map(([label, value, mono]) => (
            <div key={label}>
              <dt className="ops-label">{label}</dt>
              <dd className={mono ? "mt-1.5 font-mono tabular-nums" : "mt-1.5"}>
                {value}
              </dd>
            </div>
          ))}
          <div>
            <dt className="ops-label">Facture créée</dt>
            <dd className="mt-1.5">{displayBoolean(commande.facture_creee)}</dd>
          </div>
          <div>
            <dt className="ops-label">Facture envoyée</dt>
            <dd className="mt-1.5">{displayBoolean(commande.facture_envoyee)}</dd>
          </div>
          <div className="sm:col-span-2 lg:col-span-3 xl:col-span-4">
            <dt className="ops-label">Erreur / exception</dt>
            <dd className="mt-1.5 whitespace-pre-wrap break-words">
              {displayValue(commande.erreur_exception)}
            </dd>
          </div>
        </dl>
      </section>

      <section className="ops-panel" data-testid="commande-lines-panel">
        <div className="border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold tracking-tight">Lignes de commande</h2>
          <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
            {lignes.length} ligne{lignes.length === 1 ? "" : "s"}
          </p>
        </div>
        {lignes.length === 0 ? (
          <EmptyState
            title="Aucune ligne de commande."
            description="Le backend n’a retourné aucune ligne pour cette commande."
          />
        ) : (
          <DataTable
            columns={lineColumns}
            rows={lignes}
            rowKey={(ligne) => ligne.id}
            testId="commande-lines-table"
          />
        )}
      </section>
    </div>
  );
}
